import { Controller } from "@hotwired/stimulus"

// Interactive offer-sizing card. The statements set what the merchant can afford (the daily
// payment); the underwriter drags the factor rate and toggles daily/weekly to see the advance
// that affordable payment supports — the same math OfferSuggester uses, live:
//
//   payback  = daily_payment × TERM_PAYMENTS            (affordability-based total)
//   advance  = round(payback / factor to $500), clamped to the funder's amount policy
//   weekly view: payment ×5, count = TERM_PAYMENTS / 5  (5 business days/week, per the analyzer)
//
// "Use these terms" pushes the current sizing into the offer form so it stays editable.
export default class extends Controller {
  static values = {
    dailyPayment: Number,   // recommended_daily_payment (affordability cap)
    holdback: Number,       // holdback_pct_used
    minFactor: Number,
    maxFactor: Number,
    minAmount: Number,
    maxAmount: Number,
    termPayments: { type: Number, default: 120 },
    businessDaysPerWeek: { type: Number, default: 5 },
  }

  static targets = [
    "factor", "factorLabel", "payment", "paymentUnit", "advance", "advanceNote",
    "payback", "term", "cost", "dailyBtn", "weeklyBtn",
  ]

  connect() {
    this.frequency = "daily"
    this.render()
  }

  setDaily() { this.frequency = "daily"; this.render() }
  setWeekly() { this.frequency = "weekly"; this.render() }
  onFactorInput() { this.render() }

  // --- derived numbers -------------------------------------------------------------
  get factorRate() { return parseFloat(this.factorTarget.value) }
  get weekly() { return this.frequency === "weekly" }
  get paybackTotal() { return this.dailyPaymentValue * this.termPaymentsValue }
  get paymentCount() {
    return this.weekly ? Math.round(this.termPaymentsValue / this.businessDaysPerWeekValue) : this.termPaymentsValue
  }
  get paymentAmount() {
    return this.weekly ? this.dailyPaymentValue * this.businessDaysPerWeekValue : this.dailyPaymentValue
  }
  // Advance the affordable payback buys at this factor, rounded to $500 and clamped to policy.
  get rawAdvance() { return this.factorRate > 0 ? this.paybackTotal / this.factorRate : 0 }
  get advanceAmount() {
    const rounded = Math.round(this.rawAdvance / 500) * 500
    return Math.min(Math.max(rounded, this.minAmountValue), this.maxAmountValue)
  }
  get capped() {
    const rounded = Math.round(this.rawAdvance / 500) * 500
    return this.advanceAmount !== rounded
  }

  render() {
    this.factorLabelTarget.textContent = this.factorRate.toFixed(2)
    this.paymentTarget.textContent = this.money(this.paymentAmount)
    this.paymentUnitTarget.textContent = this.weekly ? "/week" : "/day"
    this.advanceTarget.textContent = this.money(this.advanceAmount, 0)
    this.paybackTarget.textContent = this.money(this.paybackTotal, 0)
    this.termTarget.textContent = `${this.paymentCount} ${this.weekly ? "weekly" : "daily"} payments`
    this.costTarget.textContent = this.money(this.paybackTotal - this.advanceAmount, 0)

    this.advanceNoteTarget.classList.toggle("hidden", !this.capped)
    this.dailyBtnTarget.classList.toggle("btn-active", !this.weekly)
    this.weeklyBtnTarget.classList.toggle("btn-active", this.weekly)
  }

  // Populate the offer form (same element the "Suggest terms" button fills) and let its own
  // listeners recompute. Amount → factor → term → frequency → payment → holdback.
  useTerms() {
    const form = document.querySelector("#offers form")
    if (!form) return
    const set = (name, value) => {
      const el = form.querySelector(`[name="offer[${name}]"]`)
      if (!el || value == null) return
      el.value = value
      el.dispatchEvent(new Event("input", { bubbles: true }))
    }
    set("amount", this.advanceAmount)
    set("factor_rate", this.factorRate.toFixed(2))
    set("term_value", this.paymentCount)
    set("term_unit", "payments")
    set("payment_frequency", this.frequency)
    set("payment_amount", this.paymentAmount.toFixed(2))
    if (this.holdbackValue) set("holdback_percent", this.holdbackValue)
    form.scrollIntoView({ behavior: "smooth", block: "center" })
  }

  money(n, dp = 2) {
    return "$" + Number(n).toLocaleString("en-US", { minimumFractionDigits: dp, maximumFractionDigits: dp })
  }
}
